import { useQueryClient } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type JSX,
  type PropsWithChildren
} from 'react';
import { logout } from '../api/authEndpoints';
import { AUTH_EXPIRED_EVENT } from '../api/http';
import { bridge } from '../native/bridge';
import { AuthContext, type AuthContextValue } from './authContext';
import { decodePrincipal, type Principal } from './principal';
import { clearSharedSession } from './sharedSession';
import { tokenStore } from './tokenStore';

interface AuthProviderProps {
  /** Called when the session ends by expiry or a 401 (not by an explicit sign-out). */
  onSessionExpired?: () => void;
}

const safeDecode = (token: string | null): Principal | null => {
  if (token === null) {
    return null;
  }
  try {
    return decodePrincipal(token);
  } catch {
    return null;
  }
};

export const AuthProvider = ({
  children,
  onSessionExpired
}: PropsWithChildren<AuthProviderProps>): JSX.Element => {
  const queryClient = useQueryClient();
  const token = useSyncExternalStore(tokenStore.subscribe, tokenStore.getToken, () => null);
  const principal = useMemo(() => safeDecode(token), [token]);
  // Inside the app the shell keeps the token (phase 2 N2): restore it once on boot so WebView
  // reloads (`invite://refresh`, pull-to-refresh) keep the session without a new challenge. In a
  // browser the shared cookie was already adopted at app start (`adoptSharedSession`).
  const [bootstrapped, setBootstrapped] = useState(
    () => tokenStore.getToken() !== null || !bridge.isAvailable()
  );
  useEffect(() => {
    if (bootstrapped) {
      return;
    }
    let active = true;
    void bridge
      .send<{ token?: string; expiresAt?: string } | null>('getAuthToken')
      .then((stored) => {
        if (!active || stored === null) {
          return;
        }
        const expiresAt = stored.expiresAt === undefined ? NaN : Date.parse(stored.expiresAt);
        if (typeof stored.token === 'string' && stored.token !== '' && expiresAt > Date.now()) {
          tokenStore.setToken(stored.token, new Date(expiresAt));
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) {
          setBootstrapped(true);
        }
      });
    return () => {
      active = false;
    };
  }, [bootstrapped]);

  const signIn = useCallback((value: string, expiresAt: Date) => {
    tokenStore.setToken(value, expiresAt);
  }, []);

  const signOut = useCallback(async () => {
    const hadToken = tokenStore.getToken() !== null;
    if (hadToken) {
      try {
        await logout();
      } catch {
        // The server session is optional; local sign-out always proceeds.
      }
    }
    tokenStore.clear();
    queryClient.clear();
    if (bridge.isAvailable()) {
      void bridge.send('clearAuthToken').catch(() => undefined);
    }
    // Leaving the shared cookie behind would sign the user back in on the next load.
    clearSharedSession();
  }, [queryClient]);

  // Parity with OperationsTokenExpiryMiddleware: sign out the moment the JWT expires.
  useEffect(() => {
    const expiry = tokenStore.getExpiry();
    if (token === null || expiry === null) {
      return;
    }
    const delay = Math.max(0, expiry.getTime() - Date.now());
    const timer = window.setTimeout(() => {
      tokenStore.clear();
      queryClient.clear();
      onSessionExpired?.();
    }, delay);
    return () => {
      window.clearTimeout(timer);
    };
  }, [token, queryClient, onSessionExpired]);

  useEffect(() => {
    const handler = (): void => {
      queryClient.clear();
      onSessionExpired?.();
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, handler);
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, handler);
    };
  }, [queryClient, onSessionExpired]);

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      principal,
      isAuthenticated: token !== null && principal !== null,
      bootstrapped,
      signIn,
      signOut
    }),
    [token, principal, bootstrapped, signIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
